/**
 * Send Gift Flow - Review & Send
 * ===============================
 * Step 5: Review and confirm the gift
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, User, Gift, MessageSquare, CreditCard, Lock, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendGiftReviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { category, recipient, amountCents, feeInfo, message } = location.state || {};
  
  const [isDark, setIsDark] = useState(true);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [giftId, setGiftId] = useState(null);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!category || !recipient || !amountCents) {
      navigate('/send-gift/select');
    }
  }, [category, recipient, amountCents]);

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(165, 124, 51, 0.15)',
    accent: '#D4AF37',
    gold: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)'
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  };

  const handleSendGift = async () => {
    setSending(true);
    setError(null);

    try {
      const res = await fetch(`${API}/gifts/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: recipient.id,
          category: category.id,
          amount_cents: amountCents,
          message: message || '',
          privacy_option: 'visible',
          funding_method: 'debit_card'
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setGiftId(data.gift_id);
        setSuccess(true);
      } else {
        setError(data.detail || 'Failed to send gift');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Success State
  if (success) {
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
        background: isDark 
          ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
          : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)',
        textAlign: 'center'
      }}>
        {/* Success Animation */}
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'rgba(76, 175, 80, 0.15)',
          border: '3px solid rgba(76, 175, 80, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '28px',
          animation: 'pulse 2s infinite'
        }}>
          <CheckCircle size={50} color="#4CAF50" />
        </div>

        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '28px',
          color: colors.text,
          marginBottom: '12px'
        }}>
          Gift Sent!
        </h2>

        <p style={{
          color: colors.textMuted,
          fontSize: '16px',
          marginBottom: '8px'
        }}>
          Your {formatCurrency(amountCents)} {category?.name} gift
        </p>

        <p style={{
          color: colors.text,
          fontSize: '18px',
          fontWeight: '600',
          marginBottom: '32px'
        }}>
          is on its way to {recipient?.name}!
        </p>

        {/* Gift Card Preview */}
        <div style={{
          background: colors.cardBg,
          borderRadius: '20px',
          border: `1px solid ${colors.border}`,
          padding: '24px',
          width: '100%',
          maxWidth: '340px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <span style={{ fontSize: '40px' }}>{category?.emoji}</span>
            <div>
              <div style={{ color: colors.text, fontSize: '18px', fontWeight: '700' }}>
                {category?.name}
              </div>
              <div style={{ color: '#4CAF50', fontSize: '24px', fontWeight: '700', fontFamily: 'Cinzel, serif' }}>
                {formatCurrency(amountCents)}
              </div>
            </div>
          </div>
          
          {message && (
            <div style={{
              padding: '12px',
              background: 'rgba(215, 181, 109, 0.08)',
              borderRadius: '10px',
              borderLeft: '3px solid #D7B56D'
            }}>
              <p style={{ 
                color: colors.text, 
                fontSize: '14px', 
                fontStyle: 'italic',
                margin: 0 
              }}>
                "{message}"
              </p>
            </div>
          )}
        </div>

        <button
          onClick={() => navigate('/gifts')}
          data-testid="done-btn"
          style={{
            width: '100%',
            maxWidth: '340px',
            padding: '16px',
            background: colors.gold,
            border: 'none',
            borderRadius: '14px',
            color: '#2C1B13',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(215, 181, 109, 0.4)'
          }}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '120px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          disabled={sending}
          style={{
            background: 'rgba(138, 115, 85, 0.15)',
            border: 'none',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: sending ? 'not-allowed' : 'pointer',
            color: colors.text,
            opacity: sending ? 0.5 : 1
          }}
        >
          <ChevronLeft size={22} />
        </button>
        
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontFamily: 'Cinzel, serif', 
            fontSize: '22px',
            fontWeight: '600',
            color: colors.text,
            margin: 0
          }}>
            Send a Gift
          </h1>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: '13px', 
            margin: '4px 0 0 0' 
          }}>
            Step 5 of 5 • Review & Send
          </p>
        </div>
      </div>

      {/* Step Progress */}
      <div style={{ padding: '0 20px 24px' }}>
        <div style={{ 
          display: 'flex', 
          gap: '6px'
        }}>
          {[1, 2, 3, 4, 5].map((step) => (
            <div
              key={step}
              style={{
                flex: 1,
                height: '3px',
                borderRadius: '2px',
                background: colors.gold
              }}
            />
          ))}
        </div>
      </div>

      {/* Title */}
      <div style={{ padding: '0 20px 24px', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '24px',
          color: colors.text,
          margin: '0 0 8px 0'
        }}>
          Review & Send
        </h2>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px',
          margin: 0
        }}>
          Confirm the details
        </p>
      </div>

      {/* Gift Summary Card */}
      <div style={{ padding: '0 16px 20px' }}>
        <div style={{
          background: colors.cardBg,
          borderRadius: '20px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden'
        }}>
          {/* Category Header */}
          <div style={{
            position: 'relative',
            height: '120px',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${category?.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.8) 100%)'
            }} />
            <div style={{
              position: 'relative',
              height: '100%',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '32px' }}>{category?.emoji}</span>
                <div>
                  <div style={{ color: '#FFF', fontSize: '18px', fontWeight: '700' }}>
                    {category?.name}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    Gift Experience
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div style={{ padding: '20px' }}>
            {/* Recipient */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              paddingBottom: '16px',
              borderBottom: `1px solid ${colors.border}`,
              marginBottom: '16px'
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: 'rgba(215, 181, 109, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {recipient?.photo ? (
                  <img 
                    src={recipient.photo} 
                    alt={recipient.name}
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <User size={22} color={colors.accent} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: colors.textMuted, fontSize: '12px' }}>To</div>
                <div style={{ color: colors.text, fontSize: '16px', fontWeight: '600' }}>
                  {recipient?.name}
                </div>
              </div>
            </div>

            {/* Message */}
            {message && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                paddingBottom: '16px',
                borderBottom: `1px solid ${colors.border}`,
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: 'rgba(215, 181, 109, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <MessageSquare size={20} color={colors.accent} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>Message</div>
                  <div style={{ color: colors.text, fontSize: '14px', fontStyle: 'italic' }}>
                    "{message}"
                  </div>
                </div>
              </div>
            )}

            {/* Amount Breakdown */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}>
                <span style={{ color: colors.textMuted, fontSize: '14px' }}>Gift Amount</span>
                <span style={{ color: colors.text, fontSize: '14px', fontWeight: '600' }}>
                  {feeInfo?.amount_display || formatCurrency(amountCents)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}>
                <span style={{ color: colors.textMuted, fontSize: '14px' }}>Service Fee</span>
                <span style={{ color: colors.textMuted, fontSize: '14px' }}>
                  {feeInfo?.fee_display || '$0.00'}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: `1px solid ${colors.border}`
              }}>
                <span style={{ color: colors.text, fontSize: '16px', fontWeight: '700' }}>Total</span>
                <span style={{ 
                  color: colors.accent, 
                  fontSize: '20px', 
                  fontWeight: '700',
                  fontFamily: 'Cinzel, serif'
                }}>
                  {feeInfo?.total_display || formatCurrency(amountCents)}
                </span>
              </div>
            </div>

            {/* Recipient Receives */}
            <div style={{
              padding: '12px 16px',
              background: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <span style={{ color: '#4CAF50', fontSize: '14px', fontWeight: '600' }}>
                {recipient?.name} receives {feeInfo?.amount_display || formatCurrency(amountCents)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '14px 16px',
            background: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
            borderRadius: '12px'
          }}>
            <AlertCircle size={20} color="#F44336" />
            <span style={{ color: '#F44336', fontSize: '14px' }}>{error}</span>
          </div>
        </div>
      )}

      {/* Security Note */}
      <div style={{ padding: '0 16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '12px',
          background: 'rgba(138, 115, 85, 0.1)',
          borderRadius: '10px'
        }}>
          <Lock size={14} color={colors.textMuted} />
          <span style={{ color: colors.textMuted, fontSize: '12px' }}>
            Secure payment processing
          </span>
        </div>
      </div>

      {/* Send Button */}
      <div style={{
        position: 'fixed',
        bottom: '80px',
        left: 0,
        right: 0,
        padding: '16px',
        background: isDark 
          ? 'linear-gradient(180deg, transparent 0%, #0D0D0D 30%)'
          : 'linear-gradient(180deg, transparent 0%, #FAFAFA 30%)'
      }}>
        <button
          onClick={handleSendGift}
          disabled={sending}
          data-testid="send-gift-btn"
          style={{
            width: '100%',
            padding: '18px',
            background: sending ? 'rgba(138, 115, 85, 0.5)' : colors.gold,
            border: 'none',
            borderRadius: '14px',
            color: '#2C1B13',
            fontSize: '17px',
            fontWeight: '700',
            cursor: sending ? 'not-allowed' : 'pointer',
            boxShadow: sending ? 'none' : '0 4px 20px rgba(215, 181, 109, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}
        >
          {sending ? (
            <>
              <Loader2 size={20} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
              Sending...
            </>
          ) : (
            <>
              <Gift size={20} />
              Send {feeInfo?.total_display || formatCurrency(amountCents + (feeInfo?.fee_cents || 0))}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SendGiftReviewPage;
